'use client';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Plus, ChevronDown, Brain, Activity, Pencil, MoreHorizontal, ListChecks, Search, HelpCircle, ShieldOff } from 'lucide-react';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useTranslation } from '../../lib/i18n';
import { Button } from '../../components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/shadcn/popover';
import { Input } from '../../components/ui/Input';
import { ConversationIcon } from '../../components/ui/ConversationIcon';
import { ConversationIconPicker } from '../../components/ui/ConversationIconPicker';
import { ReasoningPanel } from './ReasoningPanel';
import { ModelPicker } from './ModelPicker';
import { ProjectPicker } from './ProjectPicker';
import { NewConversationMenu } from './NewConversationMenu';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';
import { PageTopBarPortal, usePageHeader } from '../../lib/pageHeader';
import { useModelControlHost } from './ChatStatusline';
import { useConversationNaming } from './useConversationNaming';
import { MENU_ACTIONS } from '../../components/ui/menuActions';

const rowClass = 'h-auto w-full items-center justify-start gap-2 rounded-md px-2 py-1.5 text-left text-foreground';
const RenameIcon = MENU_ACTIONS.rename.icon;

/** The conversation's reasoning controls: the bar button is the trigger of an anchored popover holding
 *  ReasoningPanel, and `/reasoning` opens the same popover through the controller's `reasoningOpen` (whose
 *  setter refuses while a delegated child is focused). The historic test id stays stable for browser
 *  helpers. The level write is owned here so the popover can refuse to close while it is saving; it is scoped to the
 *  conversation and model, so it never carries one conversation's level into another. */
function ReasoningButton({ row = false }: { row?: boolean }) {
  const { t } = useTranslation();
  const { reasoningOpen, setReasoningOpen, reasoning: level } = useBrainChat();
  return (
    <Popover open={reasoningOpen} onOpenChange={(next) => { if (next || level.status !== 'saving') setReasoningOpen(next); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={row ? 'default' : 'icon'}
          type="button"
          data-testid="chat-thoughts-toggle"
          aria-label={t.reasoning.modalTitle}
          title={t.reasoning.modalTitle}
          className={row ? rowClass : undefined}
        >
          <Brain size={16} aria-hidden />
          {row ? <span>{t.reasoning.modalTitle}</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" aria-label={t.reasoning.modalTitle} className="w-[min(--spacing(88),calc(100vw_-_--spacing(4)))]">
        <ReasoningPanel level={level} />
      </PopoverContent>
    </Popover>
  );
}

/** A guest sees the owner's reasoning control in place, disabled and saying why (SPEC 1.4); the level and
 *  its panel belong to the owner, so nothing is read for it. */
function LockedReasoningButton({ row = false }: { row?: boolean }) {
  const { t } = useTranslation();
  return (
    <Button variant="ghost" size={row ? 'default' : 'icon'} type="button" disabled data-testid="chat-thoughts-toggle"
      className={row ? rowClass : undefined}
      aria-label={`${t.reasoning.modalTitle}: ${t.chat.ownerOnlyControl}`} title={t.chat.ownerOnlyControl}>
      <Brain size={16} aria-hidden />
      {row ? <span>{t.reasoning.modalTitle}</span> : null}
    </Button>
  );
}

/** Shared overflow: page chat folds reasoning here at every width; the dock keeps it on the bar.
 *  The composer's Actions menu owns the work-mode control on every surface. */
function BarOverflowMenu({ folded, desktop = false, modelHere, showPickers, onOpenTasks, onSearch, onHelp, onRevoke }: {
  /** Search, shortcuts and naming are folded; the dock also offers new conversation. */
  folded: boolean;
  /** Page chat folds reasoning here, but keeps new conversation on the bar. */
  desktop?: boolean;
  /** The bar hosts the model picker, so it rides in this menu. False while the statusline shows it, and
   *  while that is still undecided; see `useModelControlHost`. */
  modelHere: boolean;
  showPickers: boolean;
  /** Absent when the Todo plugin's `/tasks` command is not in the catalog; the row is then not offered. */
  onOpenTasks: (() => void) | null;
  onSearch?: () => void;
  onHelp: () => void;
  onRevoke?: () => void;
}) {
  const { t } = useTranslation();
  const { modelOpen, setModelOpen, openRename, reasoningOpen, setReasoningOpen, reasoning } = useBrainChat();
  const { readOnly, guest } = useBrainChatStatus();
  const { editable } = useConversationNaming();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const actionPicked = useRef(false);
  /** A row's action opens a modal or drawer whose focus scope remembers what was focused when it opened.
   *  The row itself is gone with the menu in that same commit, so focus is handed to the ⋯ trigger FIRST —
   *  that is where the overlay returns it on close, and a control that survives is the only one it can. */
  const pick = (action: () => void) => () => {
    actionPicked.current = true;
    triggerRef.current?.focus({ preventScroll: true });
    onMenuOpenChange(false);
    action();
  };
  // `/model` sets `modelOpen`; when the picker lives in this menu, the menu opens itself so the picker is
  // on screen to receive it. Closing the menu with the picker still open (outside press) resets the flag,
  // because the unmounted picker could never do it.
  useEffect(() => {
    if (modelOpen && modelHere) setOpen(true);
  }, [modelOpen, modelHere]);
  useEffect(() => {
    if (desktop && reasoningOpen) setOpen(true);
  }, [desktop, reasoningOpen]);
  const onMenuOpenChange = (next: boolean): void => {
    if (!next && desktop && reasoningOpen && reasoning.status === 'saving') return;
    setOpen(next);
    if (!next) {
      setModelOpen(false);
      if (desktop && reasoningOpen) setReasoningOpen(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={onMenuOpenChange}>
      <PopoverTrigger asChild>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="icon"
        type="button"
        data-testid="chat-bar-overflow"
        aria-label={t.chat.moreOptions}
        title={t.chat.moreOptions}
      >
        <MoreHorizontal size={18} aria-hidden />
      </Button>
      </PopoverTrigger>
      <PopoverContent data-chat-popover align="end" aria-label={t.chat.moreOptions}
        className="flex w-60 max-w-[calc(100vw_-_--spacing(6))] max-h-[var(--radix-popover-content-available-height)] flex-col gap-0.5 overflow-y-auto p-1.5"
        onCloseAutoFocus={(event) => {
          // A picked action already handed focus to its modal. Do not steal it back on unmount.
          if (actionPicked.current) event.preventDefault();
          actionPicked.current = false;
        }}>
          {folded && onSearch ? <Button type="button" variant="ghost" onClick={pick(onSearch)} className={rowClass}><Search size={16} aria-hidden /><span>{t.brainChat.ux.searchTitle}</span></Button> : null}
          {folded ? <Button type="button" variant="ghost" onClick={pick(onHelp)} className={rowClass}><HelpCircle size={16} aria-hidden /><span>{t.brainChat.ux.shortcutsTitle}</span></Button> : null}
          {desktop ? (guest ? <LockedReasoningButton row /> : <ReasoningButton row />) : null}
          {onRevoke ? <Button type="button" variant="ghost" onClick={pick(onRevoke)} className={rowClass}><ShieldOff size={16} aria-hidden /><span>{t.brainChat.ux.revokeConsents}</span></Button> : null}
          {/* Dock actions precede the wider composite pickers. */}
          {/* The row opens the project menu where it was tapped; choosing a project folds this menu away
              the same way a picked action does. */}
          {folded && !desktop && !readOnly ? (
            <NewConversationMenu align="start" onChosen={pick(() => {})}>
              <Button type="button" variant="ghost" className={rowClass}>
                <Plus size={16} className="text-muted-foreground" aria-hidden />
                <span>{t.brainChat.newChat}</span>
              </Button>
            </NewConversationMenu>
          ) : null}
          {folded && editable ? (
            <Button type="button" variant="ghost" onClick={pick(openRename)} className={rowClass}>
              <RenameIcon size={16} className="text-muted-foreground" aria-hidden />
              <span>{t.menuActions.rename}</span>
            </Button>
          ) : null}
          {/* The mobile entry opens the same task manager as `/tasks`; there is one modal and one data path. */}
          {onOpenTasks ? (
            <Button type="button" variant="ghost" onClick={pick(onOpenTasks)} className={rowClass}>
              <ListChecks size={16} className="text-muted-foreground" aria-hidden />
              <span>{t.chat.todos}</span>
            </Button>
          ) : null}
          {/* Folded away with the bar's inline copy: on a phone the statusline pill is the model control,
              and this menu is where the bar's overflow goes, not a second home for it. */}
          {modelHere ? <div className="px-1 pt-1"><ModelPicker variant="full" /></div> : null}
          {showPickers ? <div className={`px-1 pb-1 ${modelHere ? '' : 'pt-1'}`}><ProjectPicker variant="full" /></div> : null}
      </PopoverContent>
    </Popover>
  );
}

/** The open conversation's name, and the one place the chat names it. The title opens the switcher; the
 *  dock pencil, ⋯ menu and `/rename` swap it for an input in place. An unsent
 *  conversation keeps the name as the draft its first message carries; a listed one is renamed on Enter
 *  or blur through `renameSession`. Escape keeps the previous name. A read-only view (a delegated child,
 *  another account's conversation) names the conversation it belongs to (`readOnlyTitle`) and a focused
 *  child's own name beside it; it stays the switcher, so the reader can leave for another conversation,
 *  and only naming is locked (`editable`). */
function ConversationTitle({ variant, pencil }: { variant: 'full' | 'compact'; pencil: boolean }) {
  const { t } = useTranslation();
  const { activeSessionId, openHistory, conversationName, renameOpen, openRename } = useBrainChat();
  const { readOnly, readOnlyTitle, childFocusName } = useBrainChatStatus();
  const { active, unsent, editable } = useConversationNaming();
  const full = variant === 'full';
  const titleClass = full
    ? 'chat-conversation-switcher min-w-0 max-w-72 shrink justify-start gap-1 px-2 text-left text-conversation'
    : 'min-w-0 flex-1 shrink justify-start gap-1.5 px-2 text-left text-ui';
  if (editable && renameOpen) {
    return <TitleEditor key={activeSessionId} unsent={unsent} current={active?.title ?? ''} />;
  }
  const title = readOnly
    ? readOnlyTitle || t.brainChat.untitled
    : active?.title || conversationName || t.brainChat.newChat;
  const label = (
    <span className="truncate">{title}{readOnly && childFocusName ? (
      <span className="text-small text-muted-foreground">{` · ${childFocusName}`}</span>
    ) : null}</span>
  );
  return (
    <>
      {/* The conversation's name is the switcher. In the dock `aria-label` is deliberately absent: the
          visible label IS the conversation title, and an override would hide it from the accessible name.
          `aria-haspopup="dialog"` says what it opens, the shared modal rather than an anchored panel. */}
      {full ? (
        <Button
          type="button"
          variant="ghost"
          onClick={openHistory}
          aria-haspopup="dialog"
          aria-label={t.chat.openHistory}
          title={t.chat.openHistory}
          data-testid="chat-conversation-switcher"
          className={`${titleClass} text-foreground`}
        >
          {label}
          <ChevronDown size={14} className="shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      ) : (
        <Button
          type="button"
          variant="ghost"
          onClick={openHistory}
          aria-haspopup="dialog"
          className={`${titleClass} text-foreground`}
        >
          {label}
          <ChevronDown size={14} className="shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      )}
      {editable && pencil ? (
        <Button
          variant="ghost"
          size="icon"
          type="button"
          onClick={openRename}
          aria-label={t.brainChat.renameTitle}
          title={t.brainChat.renameTitle}
          data-testid="chat-conversation-rename"
          className="text-muted-foreground"
        >
          <Pencil size={14} aria-hidden />
        </Button>
      ) : null}
    </>
  );
}

function TitleEditor({ unsent, current }: { unsent: boolean; current: string }) {
  const { t } = useTranslation();
  const { conversationName, setConversationName, closeRename, renameSession } = useBrainChat();
  const [value, setValue] = useState(unsent ? conversationName : current);
  const [saving, setSaving] = useState(false);
  // Enter, Escape and the blur that follows them (or a disabled input losing focus) must settle once.
  const settling = useRef(false);
  const finish = async (save: boolean): Promise<void> => {
    if (settling.current) return;
    settling.current = true;
    const next = value.trim();
    if (unsent) {
      if (save) setConversationName(next);
      closeRename();
      return;
    }
    if (!save || !next || next === current) { closeRename(); return; }
    setSaving(true);
    try {
      await renameSession(next);
    } catch {
      // `renameSession` already told the person; the editor stays open with their text for another try.
      settling.current = false;
      setSaving(false);
    }
  };
  return (
    <Input
      autoFocus
      data-keyboard-claimed
      value={value}
      maxLength={120}
      disabled={saving}
      aria-label={t.chat.renamePlaceholder}
      placeholder={unsent ? t.chat.conversationNamePlaceholder : t.chat.renamePlaceholder}
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => { void finish(true); }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') { event.preventDefault(); void finish(true); }
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); void finish(false); }
      }}
      data-testid="chat-conversation-name"
      className="min-w-0 max-w-72 flex-1"
    />
  );
}

/** The conversation bar. Compact (dock): title + new chat. Full (/chat): the shell's top rule. Both open
 *  the ONE conversation switcher the provider owns — the dock's own popover list is gone, because two
 *  lists of the same conversations is what the switcher replaced.
 *
 *  Its own component, and memoized, for the same reason the composer is: it reads the conversation's
 *  identity and the model control, neither of which a streamed token touches. Inline in the surface's
 *  render it was rebuilt for every delta of every turn. */
export const ChatConversationBar = memo(function ChatConversationBar({ variant, mobile, telemetryShown, onOpenTelemetry, statuslineShown, onSearch }: {
  variant: 'full' | 'compact';
  mobile: boolean | undefined;
  telemetryShown?: boolean;
  onOpenTelemetry?: () => void;
  statuslineShown: boolean;
  onSearch?: () => void;
}) {
  const { t } = useTranslation();
  const { sessions, activeSessionId, conversationIcon, commands, runSlash, openHelp, revokeConversationApprovals } = useBrainChat();
  const { readOnly, childFocus, membership, guest, yolo } = useBrainChatStatus();
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  useEffect(() => { setConfirmRevoke(null); }, [activeSessionId]);
  const openShortcuts = () => openHelp('shortcuts');
  const revoke = activeSessionId && !readOnly && !childFocus && yolo === false ? () => setConfirmRevoke(activeSessionId) : undefined;
  const modelHost = useModelControlHost(statuslineShown);
  const pageHeader = usePageHeader();
  // On a phone the conversation's own controls need the bar's room, so the shell leaves out its search,
  // skin and language for as long as this bar is mounted (owner, 13 Oct 2026). Layout effect, so the
  // switch lands in the same frame as the phone controls.
  const setSecondaryActionsHidden = pageHeader?.setSecondaryActionsHidden;
  const claimsBarRoom = variant === 'full' && mobile === true;
  useLayoutEffect(() => {
    if (!claimsBarRoom || !setSecondaryActionsHidden) return undefined;
    setSecondaryActionsHidden(true);
    return () => setSecondaryActionsHidden(false);
  }, [claimsBarRoom, setSecondaryActionsHidden]);
  const tasksCommand = commands.find((candidate) => candidate.name === 'tasks');
  const active = sessions.data?.find((s) => s.id === activeSessionId);
  // The icon is the owner's to change; a guest sees it like a read-only view does.
  const iconSession = active && membership?.capabilities.editIcon && !readOnly && !childFocus ? active : null;

  const confirmation = <ConfirmDialog open={confirmRevoke !== null && confirmRevoke === activeSessionId} title={t.brainChat.ux.revokeConsents}
    description={t.brainChat.ux.revokeConsentsHint} confirmLabel={t.brainChat.ux.revokeConsents}
    onClose={() => setConfirmRevoke(null)} onConfirm={async () => { await revokeConversationApprovals(); setConfirmRevoke(null); }} />;
  if (variant === 'compact') {
    return <>
      <div className="relative flex items-center gap-1 border-b border-border px-2 py-1.5">
        {iconSession ? <ConversationIconPicker key={iconSession.id} session={iconSession} /> : <ConversationIcon icon={active?.icon ?? conversationIcon} />}
        <ConversationTitle variant="compact" pencil />
        <ProjectPicker variant="compact" />
        {/* The statusline under the conversation carries the picker when it is showing one. */}
        {modelHost === 'bar' ? <ModelPicker variant="compact" /> : null}
        {guest ? <LockedReasoningButton /> : <ReasoningButton />}
        {!readOnly ? (
          <NewConversationMenu dock>
            <Button
              variant="ghost" size="icon"
              type="button"
              aria-label={t.brainChat.newChat}
              title={t.brainChat.newChat}
            >
              <Plus size={16} aria-hidden />
            </Button>
          </NewConversationMenu>
        ) : null}
        <BarOverflowMenu folded modelHere={false} showPickers={false} onOpenTasks={null} onSearch={onSearch} onHelp={openShortcuts} onRevoke={revoke} />
      </div>
      {confirmation}
    </>;
  }

  return <>
    <PageTopBarPortal>
      <div className="chat-gutter chat-page-toolbar sticky top-0 z-[var(--z-lift)] flex min-w-0 shrink-0 items-center gap-1.5 bg-background py-2">
        {/* These controls ride in the shell's top rule at every width that publishes one — a phone
            included. Only the frameless design, which has no page slot, keeps this as its own local
            sticky bar. */}
        <div aria-hidden className="chat-page-toolbar__fade pointer-events-none absolute inset-x-0 top-full h-4 bg-gradient-to-b from-background to-transparent" />
        {/* The conversation's own name is the switcher: the one thing a reader looks for when they want
            another conversation is the name of this one. It opens the shared switcher (own list with
            search, rename and schedules; the register for an administrator) — no second list. */}
        {iconSession ? <ConversationIconPicker key={iconSession.id} session={iconSession} /> : <ConversationIcon icon={active?.icon ?? conversationIcon} />}
        <ConversationTitle variant="full" pencil={false} />
        {/* Desktop keeps the project visible; the model picker folds into the overflow when hosted here. */}
        {mobile === false ? <ProjectPicker variant="full" /> : null}
        {/* Telemetry and new conversation are one-tap actions at every page width. */}
        {onOpenTelemetry ? (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onOpenTelemetry}
            // `telemetryShown` is absent where the rail is a drawer (a phone), so the control is a plain
            // opener; where the rail is a docked column it is a toggle, and the label has to say which
            // way it goes or a hidden rail looks like a broken one. The ghost variant has no pressed
            // look, so a shown rail wears its hover fill.
            aria-label={telemetryShown ? t.telemetry.close : t.telemetry.open}
            title={telemetryShown ? t.telemetry.close : t.telemetry.open}
            aria-pressed={telemetryShown}
            data-testid="chat-bar-telemetry"
            className="aria-pressed:bg-accent aria-pressed:text-accent-foreground"
          >
            <Activity size={18} aria-hidden />
          </Button>
        ) : null}
        {!readOnly ? (
          <NewConversationMenu>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              aria-label={t.brainChat.newChat}
              title={t.brainChat.newChat}
            >
              <Plus size={18} aria-hidden />
            </Button>
          </NewConversationMenu>
        ) : null}
        {/* Phone project selection and a bar-hosted model picker live in this same overflow. */}
        {mobile !== undefined ? (
          <div>
            <BarOverflowMenu
              folded
              desktop
              modelHere={modelHost === 'bar'}
              showPickers={mobile}
              onSearch={onSearch}
              onHelp={openShortcuts}
              onRevoke={revoke}
              onOpenTasks={tasksCommand ? () => runSlash(tasksCommand) : null}
            />
          </div>
        ) : null}
      </div>
    </PageTopBarPortal>
    {confirmation}
  </>;
});
